/**
 * Who the account belongs to, and what has to be known before the app can be used
 * (B-38, D-90).
 *
 * The questions, their order, and what counts as an answer live here rather than in the
 * screen that asks them, for the usual reason (D-09): the bot will eventually have to
 * ask the same things over WhatsApp, and two lists of questions would drift the moment
 * one of them gained a field. The screen owns how a question looks; this owns what the
 * questions are.
 *
 * Pure, like the rest of core. No clock — `isOnboarded` is a statement about a document,
 * and the timestamp that marks a profile finished is stamped by the caller.
 */
export const ACCOUNT_ROLES = [
    { id: "trainee", label: "Trainee", blurb: "I work out at the gym" },
    { id: "owner", label: "Owner", blurb: "I own or head the gym" },
    { id: "trainer", label: "Personal Trainer", blurb: "I give focused sessions" },
    { id: "coach", label: "General Coach", blurb: "I support all trainees" },
];
/** Everything that is not a trainee asks the same four questions and lands in the same place. */
export function isStaff(role) {
    return role !== "trainee";
}
export const SEXES = [
    { id: "male", label: "Male" },
    { id: "female", label: "Female" },
    { id: "other", label: "Other" },
];
export const FITNESS_GOALS = [
    { id: "strength", label: "Get stronger", blurb: "Heavier lifts, lower reps" },
    { id: "muscle", label: "Build muscle", blurb: "Size and volume" },
    { id: "fatLoss", label: "Lose fat", blurb: "Leaner at the same strength" },
    // Added B-44: "just get a bit fit" had no home among five specific targets.
    { id: "general", label: "Get generally fit", blurb: "Feel better, no single target" },
    { id: "endurance", label: "Build endurance", blurb: "Last longer, recover faster" },
    { id: "mobility", label: "Move better", blurb: "Mobility and pain-free range" },
    { id: "consistency", label: "Stay consistent", blurb: "Show up, week after week" },
];
export const BODY_TYPES = [
    { id: "slim", label: "Slim", blurb: "Lean, not much muscle yet" },
    { id: "average", label: "Average", blurb: "Neither especially lean nor heavy" },
    { id: "athletic", label: "Athletic", blurb: "Lean with some muscle" },
    { id: "muscular", label: "Muscular", blurb: "Clearly built" },
    { id: "heavier", label: "Heavier", blurb: "Carrying more weight than I'd like" },
];
export const JOINT_AREAS = [
    "knee",
    "lowerBack",
    "shoulder",
    "wristElbow",
    "ankleFoot",
    "hip",
    "neck",
];
export const CONDITIONS = [
    { id: "knee", label: "Knee", group: "joints" },
    { id: "lowerBack", label: "Lower back", group: "joints" },
    { id: "shoulder", label: "Shoulder", group: "joints" },
    { id: "wristElbow", label: "Wrist / elbow", group: "joints" },
    { id: "ankleFoot", label: "Ankle / foot", group: "joints" },
    { id: "hip", label: "Hip", group: "joints" },
    { id: "neck", label: "Neck", group: "joints" },
    { id: "heart", label: "Heart condition", group: "medical" },
    { id: "bloodPressure", label: "High blood pressure", group: "medical" },
    { id: "recentSurgery", label: "Recent surgery", group: "medical" },
    { id: "implant", label: "Metal implant / rod", group: "medical" },
    { id: "pregnancy", label: "Pregnancy", group: "medical" },
    { id: "other", label: "Something else", group: "medical" },
];
/**
 * The conditions where intense work is a risk to more than a joint. A plan still builds
 * for them — refusing would push the person to train with no plan at all — but it drops
 * high-intensity exercises and says to check it with a doctor (C-26).
 */
export const RED_FLAGS = ["heart", "bloodPressure", "recentSurgery", "pregnancy"];
export const MAX_NOTE_CHARS = 200;
/**
 * The stored form of a health answer: list order, one entry per id, notes trimmed and
 * capped, a blank note absent rather than `""` (and never `undefined`, which Firestore
 * rejects).
 */
export function normaliseHealth(health) {
    if ("none" in health)
        return { none: true };
    const byId = new Map();
    for (const c of health.conditions) {
        const note = c.note?.trim().slice(0, MAX_NOTE_CHARS);
        if (!byId.has(c.id) || (note && !byId.get(c.id)))
            byId.set(c.id, note || undefined);
    }
    const conditions = [];
    for (const { id } of CONDITIONS) {
        if (!byId.has(id))
            continue;
        const note = byId.get(id);
        conditions.push(note ? { id, note } : { id });
    }
    return { conditions };
}
export function recordedConditions(health) {
    return health && "conditions" in health ? health.conditions : [];
}
export function hasRedFlag(health) {
    return recordedConditions(health).some((c) => RED_FLAGS.includes(c.id));
}
export const EXPERIENCES = [
    { id: "new", label: "New to this", blurb: "Under six months of regular training" },
    { id: "some", label: "Some experience", blurb: "Comfortable with the main lifts" },
    { id: "experienced", label: "Experienced", blurb: "Years of consistent training" },
];
export const SESSION_MINUTES = [30, 45, 60, 90];
/**
 * The bounds on an age. Wide on purpose: this is a typo check, not a policy about who
 * may lift. `MIN_AGE` is where a stray single digit stops being plausible.
 */
export const MIN_AGE = 10;
export const MAX_AGE = 100;
/**
 * The questions this role is asked, after the role itself.
 *
 * A trainee is asked the three things a score depends on, then the three a plan is built
 * from (B-44) — those last three skippable, see `SKIPPABLE_STEPS`. Everyone else is asked who they are and where they are — no more, because nothing
 * in the app reads those fields yet and a question whose answer is never used is a
 * question that should not be asked (D-79).
 */
export function stepsFor(role) {
    return isStaff(role)
        ? ["name", "gym", "gymLink"]
        : ["sex", "age", "bodyweight", "bodyType", "goals", "health"];
}
/**
 * The questions that may be left for later (B-44). None of them is needed to log or to
 * score; all three are needed to build a plan, and that flow asks for whichever is
 * missing instead.
 */
export const SKIPPABLE_STEPS = new Set(["bodyType", "goals", "health"]);
/**
 * Whether the answer to one question is good enough to move on.
 *
 * The screen calls this rather than carrying its own idea of a valid answer, so the
 * Continue button and the eventual write agree about what was required.
 */
export function isStepAnswered(step, answers) {
    switch (step) {
        case "role":
            return answers.role !== undefined;
        case "sex":
            return answers.sex !== undefined;
        case "age":
            return (answers.age !== undefined &&
                Number.isInteger(answers.age) &&
                answers.age >= MIN_AGE &&
                answers.age <= MAX_AGE);
        case "bodyweight":
            return answers.bodyweightKg !== undefined && answers.bodyweightKg > 0;
        case "bodyType":
            return answers.bodyType !== undefined;
        case "goals":
            return (answers.goals?.length ?? 0) > 0;
        case "health": {
            const health = answers.health;
            return health !== undefined && ("none" in health || health.conditions.length > 0);
        }
        case "name":
            return (answers.name ?? "").trim().length > 0;
        case "gym":
            return answers.gym !== undefined;
        // The one question that may be left empty. Answered either way, so Continue is never
        // disabled on it; `profilePatch` simply omits the field when nothing was typed.
        case "gymLink":
            return true;
    }
}
/**
 * Everything asked of this role, answered.
 *
 * Note it takes the role from `answers` rather than being told — a half-filled trainee
 * answer set does not become complete by switching the role to one that asks different
 * questions, because switching clears nothing; the steps are re-read from the role each
 * time this is called.
 */
export function isComplete(answers) {
    if (answers.role === undefined)
        return false;
    return stepsFor(answers.role)
        .filter((step) => !SKIPPABLE_STEPS.has(step))
        .every((step) => isStepAnswered(step, answers));
}
/**
 * The fields to merge into `users/{uid}`.
 *
 * Two things this deliberately does not do. It never writes `undefined` — Firestore
 * rejects the value, and a question this role was not asked should leave no trace on the
 * document rather than an empty one; `exactOptionalPropertyTypes` makes that a compile
 * error rather than a convention, which is why every field is assigned behind a check.
 * And it stores **`birthYear`, not the age typed**: an age is only true for a year, and a
 * number that silently rots is worse than one that has to be subtracted. `thisYear` is passed in because core does not own a clock.
 *
 * `currentBodyweightKg` is the field the score already reads (it is not a new one), so a
 * trainee's answer here is the same answer the Profile screen edits later.
 */
export function profilePatch(answers, thisYear, now) {
    const role = answers.role;
    if (role === undefined)
        throw new Error("profilePatch called before a role was chosen");
    const patch = { role, onboardedAt: now };
    if (isStaff(role)) {
        const name = answers.name?.trim();
        const link = answers.gymMapsUrl?.trim();
        if (name)
            patch.name = name;
        if (answers.gym)
            patch.gym = answers.gym;
        if (link)
            patch.gymMapsUrl = link;
    }
    else {
        if (answers.sex !== undefined)
            patch.sex = answers.sex;
        if (answers.age !== undefined)
            patch.birthYear = thisYear - answers.age;
        if (answers.bodyweightKg !== undefined)
            patch.currentBodyweightKg = answers.bodyweightKg;
        if (answers.bodyType !== undefined)
            patch.bodyType = answers.bodyType;
        if (answers.goals !== undefined && answers.goals.length > 0)
            patch.goals = answers.goals;
        if (answers.health !== undefined)
            patch.health = normaliseHealth(answers.health);
    }
    return patch;
}
/**
 * Whether this profile has been through the flow.
 *
 * Keyed on `onboardedAt` rather than on the presence of the fields, because the fields
 * are the answers and an answer can legitimately be absent — a skipped maps link, a role
 * that was never asked for a bodyweight. One timestamp means "these questions have been
 * put to this person", which is the thing the gate actually needs to know.
 *
 * A profile the bot created for a number that has only ever messaged WhatsApp has no
 * `onboardedAt`, so the first dashboard sign-in on that account asks. That is intended:
 * the questions have genuinely never been asked.
 */
export function isOnboarded(profile) {
    return typeof profile?.onboardedAt === "number";
}
//# sourceMappingURL=onboarding.js.map